import {
  HELP_CENTRE_URL,
  INFO_EMAIL,
  SUPPORT_EMAIL,
  WATERCRAFT_URL,
} from '../../../../../AppConstants';
import { LEGAL_PATHS } from '../../../../legal/utils/legalPaths';
import {
  buildSearchPath,
  buildSearchTagPath,
  SEARCH_TAG_IDS,
} from '../../../../search/utils/searchUrls';

export const FOOTER_LINK_ACTIONS = {
  CHAT: 'chat',
  COOKIE_SETTINGS: 'cookie_settings',
};

const LIST_YOUR_LAKES_PATH = '/swimbooker-fishery-manager';

export function getVisibleFooterLinks(links, enabledFlags = {}) {
  return links.filter(({ to, href, flag }) => {
    if (flag && !enabledFlags[flag]) {
      return false;
    }
    return Boolean(to || href);
  });
}

export const FOOTER_COLUMNS = [
  {
    key: 'anglers',
    title: 'For Anglers',
    links: [
      { label: 'Find venues', to: '/search' },
      { label: 'UK fisheries', to: '/search' },
      {
        label: 'European fisheries',
        to: buildSearchTagPath(SEARCH_TAG_IDS.EUROPE),
      },
      {
        label: 'Thailand fisheries',
        to: buildSearchTagPath(SEARCH_TAG_IDS.THAILAND),
      },
      { label: 'swimbooker+', href: process.env.REACT_APP_SBPLUS_URL },
      { label: 'Watercraft app', href: WATERCRAFT_URL },
      {
        label: 'Events and Tuition',
        to: '/events/search',
        flag: 'angling_trust_events',
      },
      { label: 'Angling Social', to: '/articles' },
      { label: 'Create free account', to: '/join' },
      { label: 'My profile', to: '/user/profile' },
    ],
  },
  {
    key: 'fisheries',
    title: 'For Fisheries',
    links: [
      { label: 'List your lakes', to: LIST_YOUR_LAKES_PATH },
      { label: 'Why swimbooker', to: LIST_YOUR_LAKES_PATH },
      { label: 'Claim your fishery profile', to: '/contact/fishery' },
      { label: 'Fishery login', to: '/admin/login' },
      { label: 'Fishery management tools', to: LIST_YOUR_LAKES_PATH },
      { label: 'Pricing & fees', to: `${LIST_YOUR_LAKES_PATH}#get-in-touch` },
      { label: 'Case studies', to: null },
    ],
  },
  {
    key: 'company',
    title: 'Company',
    links: [
      { label: 'About us', to: '/about' },
      { label: 'News & blog', to: '/articles' },
      { label: 'Press', href: `mailto:${INFO_EMAIL}` },
      { label: 'Careers', href: `mailto:${INFO_EMAIL}` },
      { label: 'Contact us', to: '/contact' },
    ],
  },
  {
    key: 'support',
    title: 'Support',
    links: [
      { label: 'Help centre', href: HELP_CENTRE_URL },
      { label: 'Cancellations & refunds', to: '/contact/anglers' },
      { label: 'Complaints', to: LEGAL_PATHS.COMPLAINTS },
      { label: 'Email Support Team', href: `mailto:${SUPPORT_EMAIL}` },
      {
        label: 'Chat with us',
        action: FOOTER_LINK_ACTIONS.CHAT,
        to: HELP_CENTRE_URL,
      },
    ],
  },
  {
    key: 'legal',
    title: 'Legal',
    links: [
      { label: 'Angler terms', to: LEGAL_PATHS.ANGLER_TERMS },
      {
        label: 'swimbooker+ terms',
        to: LEGAL_PATHS.SWIMBOOKER_PLUS_TERMS,
      },
      {
        label: 'Venue partner terms',
        to: LEGAL_PATHS.VENUE_PARTNER_TERMS,
      },
      { label: 'Privacy policy', to: LEGAL_PATHS.PRIVACY_POLICY },
      { label: 'Cookie policy', to: LEGAL_PATHS.COOKIE_POLICY },
      {
        label: 'Cookie settings',
        action: FOOTER_LINK_ACTIONS.COOKIE_SETTINGS,
        to: LEGAL_PATHS.COOKIE_POLICY,
      },
      // Hidden until the accessibility statement is published as a legal page.
      { label: 'Accessibility statement', to: null },
      { label: 'Sitemap', to: '/sitemap' },
    ],
  },
];

// Text search is fuzzy: "Berkshire" currently returns mostly Yorkshire venues, so it stays hidden until county hub pages exist.
const COUNTIES = [
  { name: 'Kent', isSearchable: true },
  { name: 'Essex', isSearchable: true },
  { name: 'Sussex', isSearchable: true },
  { name: 'Hampshire', isSearchable: true },
  { name: 'Surrey', isSearchable: true },
  { name: 'Berkshire', isSearchable: false },
  { name: 'Oxfordshire', isSearchable: true },
  { name: 'Cambridgeshire', isSearchable: true },
  { name: 'Norfolk', isSearchable: true },
  { name: 'Yorkshire', isSearchable: true },
];

export const FOOTER_LOCATION_GROUPS = [
  {
    key: 'county',
    title: 'Fishing by county',
    links: COUNTIES.map(({ name, isSearchable }) => ({
      label: `Fishing lakes in ${name}`,
      to: isSearchable ? buildSearchPath({ query: name }) : null,
    })),
  },
  {
    key: 'type',
    title: 'Fishing by type',
    links: [
      {
        label: 'Carp fishing lakes',
        to: buildSearchPath({ filters: { species: ['Carp'] } }),
      },
      {
        label: 'Day ticket fisheries',
        to: buildSearchTagPath(SEARCH_TAG_IDS.WATERCRAFT_TICKET),
      },
      {
        label: 'Night fishing lakes',
        to: buildSearchPath({ filters: { quick_facts: ['Night Fishing'] } }),
      },
      {
        label: 'Lake exclusives',
        to: buildSearchTagPath(SEARCH_TAG_IDS.LAKE_EXCLUSIVE),
      },
      {
        label: 'Match fishing lakes',
        to: buildSearchTagPath(SEARCH_TAG_IDS.MATCH_LAKES),
      },
      {
        label: 'Predator fishing',
        to: buildSearchPath({ filters: { species: ['Pike'] } }),
      },
      {
        label: 'Fishing holidays with accommodation',
        to: buildSearchTagPath(SEARCH_TAG_IDS.LODGES),
      },
      // Only 7 trout venues at present, too thin to link. Enable once there are more.
      { label: 'Trout fisheries', to: null },
    ],
  },
];
